import { Button, Typography } from "@mui/material";
import { useCallback } from "react";
import {
  useChargePatientNowMutation,
  useVoidInvoiceMutation,
} from "src/api/services/invoicesApi";
import { Column, Invoice } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useInvoiceModal } from "src/context/InvoiceModalProvider/useInvoiceModal";
import { isError } from "src/utils/isError";
import CouponsCell from "../components/CouponsCell";
import ChargeAttemptCell from "../components/ChargeAttemptCell";
import { format } from "date-fns";

export const useColumns = (patientId: number): Column<Invoice>[] => {
  const { handleOpenCreditNote } = useInvoiceModal();
  const [voidInvoice, { isLoading: isLoadingVoid }] = useVoidInvoiceMutation();
  const [chargePatientNow, { isLoading }] = useChargePatientNowMutation();

  const handleChargeNow = useCallback(
    async (row: Invoice) => {
      if (row.id == null) return null;
      if (row.external_id == null) return null;

      try {
        await chargePatientNow({
          invoiceId: row.external_id,
        }).unwrap();
      } catch (error: unknown) {
        if (isError(error)) {
          alert(error.data.message);
        } else {
          alert(`Error charging patient: ${error}`);
          console.error("Error charging patient", error);
        }
      }
    },
    [chargePatientNow],
  );

  const handleVoid = useCallback(
    async (row: Invoice) => {
      if (row.external_id == null) return null;
      try {
        await voidInvoice(row.external_id).unwrap();
      } catch (error: unknown) {
        if (isError(error)) {
          alert(error.data.message);
        } else {
          alert(`Error voiding invoice: ${error}`);
          console.error("Error voiding invoice", error);
        }
      }
    },
    [voidInvoice],
  );

  return [
    {
      id: "status",
      label: "Status",
      minWidth: 60,
      align: "center",
      format: (value) => {
        return value.status;
      },
      render: (row) => {
        return <Typography>{row.status}</Typography>;
      },
      orderable: false,
    },
    {
      id: "description",
      label: "Description",
      minWidth: 200,
      align: "center",
      format: (value) =>
        value.description != null ? value.description : "N/A",
      orderable: false,
      render: (row) => (
        <Typography>
          {row.description != null ? row.description : "N/A"}
        </Typography>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      minWidth: 100,
      align: "center",
      format: (row) => `$${row.amount.toFixed(2)}`,
      orderable: false,
      render: (row) => <Typography>{`$${row.amount.toFixed(2)}`}</Typography>,
    },
    {
      id: "paid_at",
      label: "Paid at",
      minWidth: 150,
      align: "center",
      format: (row) => row.id.toString() ?? "N/A",
      orderable: false,
      render: (row) => (
        <Typography>
          {row.paid_at != null
            ? format(new Date(row.paid_at), "MM/dd/yyyy")
            : "N/A"}
        </Typography>
      ),
    },
    {
      id: "refund_amount",
      label: "Refund amount",
      minWidth: 120,
      align: "center",
      format: (row) => {
        if (row.refund_amount == null) return "N/A";

        return `$${row.refund_amount.toFixed(2)}`;
      },
      render: (row) => {
        if (row.refund_amount == null) return "N/A";

        return <Typography>{`$${row.refund_amount.toFixed(2)}`}</Typography>;
      },
      orderable: false,
    },
    {
      id: "next_attempt_at",
      label: "Next attempt at",
      minWidth: 150,
      align: "center",
      format: (row) => row.id.toString() ?? "N/A",
      orderable: false,
      render: (row) => (
        <ChargeAttemptCell attempt="next" invoiceId={row.external_id} />
      ),
    },
    {
      id: "charge_now",
      label: "Charge patient now?",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: () => "Charge",
      render: (row) => (
        <LoadingButton
          loading={isLoading}
          onClick={() => handleChargeNow(row)}
          disabled={row.status !== "open"}
        >
          Charge
        </LoadingButton>
      ),
    },
    {
      id: "last_charge_attempt_at",
      label: "Last charge attempt date",
      minWidth: 180,
      align: "center",
      orderable: false,
      format: (row) => {
        return row.id.toString() ?? "N/A";
      },
      render: (row) => {
        return <ChargeAttemptCell attempt="last" invoiceId={row.external_id} />;
      },
    },
    {
      id: "credit_note",
      label: "Add credit note",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: () => "Add",
      render: (row) => (
        <Button
          onClick={() => handleOpenCreditNote(row)}
          disabled={["paid", "refunded", "partially_refunded", "void"].includes(
            row.status,
          )}
        >
          Add
        </Button>
      ),
    },
    {
      id: "void",
      label: "Void invoice",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: () => "Void",
      render: (row) => (
        <LoadingButton
          loading={isLoadingVoid}
          onClick={() => handleVoid(row)}
          disabled={["paid", "void"].includes(row.status)}
        >
          Void
        </LoadingButton>
      ),
    },
    {
      id: "coupon",
      label: "Coupon",
      minWidth: 150,
      align: "center",
      orderable: false,
      format: (row) => row?.external_id ?? "",
      render: (row) => (
        <CouponsCell invoiceId={row.external_id} patientId={patientId} />
      ),
    },
  ];
};
